/**
 * Miscellaneous helper functions for formatting and data transformation.
 * @module utils/helpers
 */

import type {
  DeliveryPattern,
  ElectiveDefinition,
  Module,
  PLO,
  Programme,
  ProgrammeVersion,
} from "@/types";

import { escapeHtml } from "./dom";

/**
 * Deterministic mapping from QQI award type to NFQ level.
 * Used to auto-fill the NFQ level when a known award type is selected.
 */
export const AWARD_TYPE_NFQ_MAP: Readonly<Record<string, number>> = {
  "Higher Certificate": 6,
  "Ordinary Bachelor Degree": 7,
  "Honours Bachelor Degree": 8,
  "Higher Diploma": 8,
  "Postgraduate Diploma": 9,
  Masters: 9,
  Doctoral: 10,
};

/** A QQI-valid credit (ECTS) range for an award. */
export interface QqiCreditRange {
  min: number;
  max: number;
}

/**
 * QQI-valid ECTS credit volumes for the major higher-education award types.
 * These reflect the standard credit ranges QQI applies to each award class.
 */
const AWARD_TYPE_CREDIT_RANGE: Readonly<Record<string, QqiCreditRange>> = {
  "Higher Certificate": { min: 120, max: 120 },
  "Ordinary Bachelor Degree": { min: 180, max: 180 },
  "Honours Bachelor Degree": { min: 180, max: 240 },
  "Higher Diploma": { min: 60, max: 60 },
  "Postgraduate Diploma": { min: 60, max: 60 },
  Masters: { min: 60, max: 120 },
  Doctoral: { min: 180, max: 360 },
};

/**
 * Fallback ECTS credit ranges keyed by NFQ level, used when the award type is
 * unknown (e.g. "Other") but an NFQ level is available.
 */
const NFQ_LEVEL_CREDIT_RANGE: Readonly<Record<number, QqiCreditRange>> = {
  6: { min: 60, max: 120 },
  7: { min: 180, max: 180 },
  8: { min: 60, max: 240 },
  9: { min: 60, max: 120 },
  10: { min: 180, max: 360 },
};

/**
 * Returns the QQI-valid ECTS credit range for a given award type and NFQ level.
 *
 * The award type takes precedence; when it is unknown, the NFQ level provides a
 * fallback range. When neither is recognised, a zero range is returned to signal
 * that no suggestion is available.
 *
 * @example
 * getQqiCreditRange("Higher Certificate", 6);     // { min: 120, max: 120 }
 * getQqiCreditRange("Honours Bachelor Degree", 8); // { min: 180, max: 240 }
 */
export function getQqiCreditRange(awardType: string, nfqLevel: number): QqiCreditRange {
  const byType = AWARD_TYPE_CREDIT_RANGE[awardType];
  if (byType) {
    return byType;
  }
  const byLevel = NFQ_LEVEL_CREDIT_RANGE[nfqLevel];
  if (byLevel) {
    return byLevel;
  }
  return { min: 0, max: 0 };
}

/**
 * Formats a {@link QqiCreditRange} as a short human-readable string, or an empty
 * string when no range is available (max of 0).
 *
 * @example
 * formatQqiCreditRange({ min: 60, max: 60 });   // "60 ECTS"
 * formatQqiCreditRange({ min: 180, max: 240 }); // "180–240 ECTS"
 * formatQqiCreditRange({ min: 0, max: 0 });     // ""
 */
export function formatQqiCreditRange(range: QqiCreditRange): string {
  if (range.max <= 0) {
    return "";
  }
  if (range.min === range.max) {
    return `${range.min} ECTS`;
  }
  return `${range.min}\u2013${range.max} ECTS`;
}

/**
 * Formats a numeric value as a percentage string with the "%" suffix.
 *
 * @example
 * formatPct(75);    // "75%"
 * formatPct("50");  // "50%"
 * formatPct();      // "0%"
 */
export function formatPct(n?: number | string): string {
  const x = Number(n ?? 0);
  return `${x}%`;
}

/**
 * Normalises persisted and imported delivery labels to the canonical values
 * used by the editor and export mappers.
 */
export function normalizeDeliveryModality(modality?: string): string {
  const normalized = (modality ?? "").trim().toUpperCase().replaceAll("_", " ");
  if (["FACE TO FACE", "FACE-TO-FACE", "ONSITE", "ON-SITE", "F2F"].includes(normalized)) {
    return "F2F";
  }
  if (["ONLINE", "FULLY ONLINE", "FOL"].includes(normalized)) {
    return "ONLINE";
  }
  if (["BLENDED", "HYBRID"].includes(normalized)) {
    return "BLENDED";
  }
  if (normalized === "APPRENTICESHIP") {
    return "APPRENTICESHIP";
  }
  return normalized;
}

/**
 * Returns the default delivery pattern percentages for a given modality.
 *
 * @example
 * defaultPatternFor("F2F");     // { syncOnlinePct: 0, asyncDirectedPct: 0, onCampusPct: 100 }
 * defaultPatternFor("ONLINE");  // { syncOnlinePct: 40, asyncDirectedPct: 60, onCampusPct: 0 }
 */
export function defaultPatternFor(mod: string): {
  syncOnlinePct: number;
  asyncDirectedPct: number;
  onCampusPct: number;
} {
  const canonicalModality = normalizeDeliveryModality(mod);
  if (canonicalModality === "F2F") {
    return { syncOnlinePct: 0, asyncDirectedPct: 0, onCampusPct: 100 };
  }
  if (canonicalModality === "ONLINE") {
    return { syncOnlinePct: 40, asyncDirectedPct: 60, onCampusPct: 0 };
  }
  // BLENDED default
  return { syncOnlinePct: 30, asyncDirectedPct: 40, onCampusPct: 30 };
}

/** Calculates the total of all delivery pattern percentages. */
export function sumPattern(pat: {
  syncOnlinePct?: number;
  asyncDirectedPct?: number;
  onCampusPct?: number;
}): number {
  return (
    Number(pat.syncOnlinePct ?? 0) +
    Number(pat.asyncDirectedPct ?? 0) +
    Number(pat.onCampusPct ?? 0)
  );
}

/** Calculates the total credits for modules assigned to a stage. */
export function sumStageCredits(
  allModules: { id: string; credits?: number }[],
  stageModules: { moduleId: string }[],
): number {
  const ids = (stageModules ?? []).map((x) => x.moduleId);
  return (allModules ?? [])
    .filter((m) => ids.includes(m.id))
    .reduce((acc, m) => acc + Number(m.credits ?? 0), 0);
}

/**
 * Calculates effective credits for a stage, accounting for elective definitions.
 * Mandatory module credits are summed directly, while elective modules contribute
 * their definition's credit requirement (not the sum of all elective module credits).
 *
 * @example
 * // 3 mandatory modules (10cr each) + 1 elective definition requiring 10cr
 * // (with 3 elective modules of 5cr each in the definition)
 * getEffectiveCredits(programme, stageModuleIds) // => 40 (30 + 10)
 */
export function getEffectiveCredits(
  p: Programme,
  stageModuleIds: string[],
): {
  mandatoryCredits: number;
  electiveCredits: number;
  total: number;
} {
  const modules = p.modules ?? [];
  const electiveDefinitions = p.electiveDefinitions ?? [];
  const stageIdSet = new Set(stageModuleIds);

  // Build a set of all module IDs that belong to any elective definition
  const electiveDefModuleIds = new Set<string>();
  let electiveCredits = 0;

  electiveDefinitions.forEach((def) => {
    const defModIds = (def.groups ?? []).flatMap((g) => g.moduleIds ?? []);
    // If any module in this definition is assigned to the stage, count the definition credits
    if (defModIds.some((id) => stageIdSet.has(id))) {
      defModIds.forEach((id) => electiveDefModuleIds.add(id));
      electiveCredits += Number(def.credits ?? 0);
    }
  });

  // Sum mandatory credits (modules in stage that are NOT in an elective definition)
  const mandatoryCredits = modules
    .filter((m) => stageIdSet.has(m.id) && !electiveDefModuleIds.has(m.id))
    .reduce((acc, m) => acc + Number(m.credits ?? 0), 0);

  return {
    mandatoryCredits,
    electiveCredits,
    total: mandatoryCredits + electiveCredits,
  };
}

/**
 * Credits a learner actually earns from a single elective definition.
 *
 * A learner completes one option (the representative first group) from the
 * definition, so the credits taken equal that group's module credits, capped
 * by the definition's stated requirement. When the group has no modules (or the
 * definition has no groups) the stated requirement is used; when the definition
 * has no stated credits the representative group's module credits are used.
 */
export function learnerElectiveCreditsForDefinition(
  def: ElectiveDefinition,
  modules: Module[],
): number {
  const defCredits = Number(def.credits ?? 0);
  const groups = def.groups ?? [];
  if (groups.length === 0) {
    return defCredits;
  }

  const firstGroupIds = new Set(groups[0]?.moduleIds ?? []);
  const groupSum = modules
    .filter((m) => firstGroupIds.has(m.id))
    .reduce((acc, m) => acc + Number(m.credits ?? 0), 0);

  if (groupSum === 0) {
    return defCredits;
  }
  if (defCredits === 0) {
    return groupSum;
  }
  return Math.min(defCredits, groupSum);
}

/**
 * Calculates effective programme-level credits, counting only the elective
 * credits a learner actually takes rather than every available option.
 *
 * Without definitions: mandatory + elective module credits (raw sum).
 * With definitions: mandatory module credits + the learner-taken credits of each
 * definition (the chosen option per {@link learnerElectiveCreditsForDefinition}).
 */
export function getEffectiveProgrammeCredits(p: Programme): {
  mandatoryCredits: number;
  electiveCredits: number;
  total: number;
} {
  const modules = p.modules ?? [];
  const electiveDefinitions = p.electiveDefinitions ?? [];

  const mandatoryModules = modules.filter((m) => !m.isElective);
  const mandatoryCredits = mandatoryModules.reduce((acc, m) => acc + Number(m.credits ?? 0), 0);

  if (electiveDefinitions.length === 0) {
    // No definitions — fall back to raw elective module sum
    const electiveModules = modules.filter((m) => m.isElective === true);
    const electiveCredits = electiveModules.reduce((acc, m) => acc + Number(m.credits ?? 0), 0);
    return { mandatoryCredits, electiveCredits, total: mandatoryCredits + electiveCredits };
  }

  // With definitions — count the learner-taken pathway (one option per definition)
  const electiveCredits = electiveDefinitions.reduce(
    (acc, def) => acc + learnerElectiveCreditsForDefinition(def, modules),
    0,
  );
  return { mandatoryCredits, electiveCredits, total: mandatoryCredits + electiveCredits };
}

const DELIVERY_MODALITY_LABELS: Partial<Record<string, string>> = {
  F2F: "Face-to-face",
  BLENDED: "Blended",
  ONLINE: "Fully online",
};

/**
 * Generates an HTML summary of delivery pattern percentages for display.
 * Returns an em-dash span if no modality is set.
 */
export function deliveryPatternsHtml(p: {
  deliveryModality?: string;
  deliveryPatterns?: unknown;
}): string {
  const mod = p.deliveryModality;
  const patterns: Partial<Record<string, DeliveryPattern>> =
    p.deliveryPatterns && typeof p.deliveryPatterns === "object"
      ? (p.deliveryPatterns as Partial<Record<string, DeliveryPattern>>)
      : {};
  if (!mod) {
    return '<span class="text-muted">—</span>';
  }

  const label = (k: string) => DELIVERY_MODALITY_LABELS[k] ?? k;
  const pat = patterns[mod] ?? defaultPatternFor(mod);
  const a = Number(pat.syncOnlinePct ?? 0);
  const b = Number(pat.asyncDirectedPct ?? 0);
  const c = Number(pat.onCampusPct ?? 0);
  return `<div><span class="fw-semibold">${escapeHtml(label(mod))}:</span> ${a}% sync online, ${b}% async directed, ${c}% on campus</div>`;
}

/**
 * Extracts the text content from a learning outcome item (PLO or MIMLO).
 * Handles both legacy string format and current object format.
 */
export function itemText(x: unknown): string {
  if (typeof x === "string") {
    return x;
  }
  if (x && typeof x === "object") {
    return (x as { text?: string }).text ?? "";
  }
  return "";
}

/** Alias for {@link itemText} — extracts text from a MIMLO. */
export const mimloText = itemText;

/** Alias for {@link itemText} — extracts text from a PLO. */
export const ploText = itemText;

/**
 * Migrates module MIMLOs from legacy string array format to object format.
 * Modifies the module in place, converting string[] to {id, text}[].
 */
export function ensureMimloObjects(draft: Module & { mimlos?: unknown[] }): void {
  draft.mimlos ??= [];
  if (draft.mimlos.length && typeof draft.mimlos[0] === "string") {
    draft.mimlos = draft.mimlos.map((t: unknown) => ({
      id: `mimlo_${crypto.randomUUID()}`,
      text: String(t ?? ""),
    }));
  }
}

/**
 * Migrates programme PLOs from legacy string array format to object format.
 * Modifies the programme in place, converting string[] to {id, text, standardId}[].
 */
export function ensurePloObjects(draft: Programme): void {
  draft.plos ??= [];
  if (draft.plos.length && typeof draft.plos[0] === "string") {
    draft.plos = (draft.plos as unknown as unknown[]).map((t: unknown) => ({
      id: `plo_${crypto.randomUUID()}`,
      text: String(t ?? ""),
      standardId: null,
    })) as PLO[];
  }
}

/**
 * Find a programme version by ID.
 */
export function findVersion(
  versions: ProgrammeVersion[],
  id: string,
): ProgrammeVersion | undefined {
  return versions.find((v) => v.id === id);
}

/**
 * Find a module by ID.
 */
export function findModule(modules: Module[], id: string): Module | undefined {
  return modules.find((m) => m.id === id);
}

/**
 * Resolve effort hours for a module, falling back to the first available
 * version key if the requested one is not present.
 */
export function resolveEffortHours(mod: Module, versionKey: string): Record<string, number> {
  return (mod.effortHours?.[versionKey] ??
    mod.effortHours?.[Object.keys(mod.effortHours ?? {})[0]] ??
    {}) as Record<string, number>;
}
